// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { documentsApi } from '@shared/data-access/api/engine/documents-api';
import type { RootDocumentsRepositoryState } from '@shared/data-access/interfaces/redux/documents-repository-state-types';
import {
  selectDocumentsForClientScope,
  selectDocumentsLinkedToEntityForClient,
} from '@shared/data-access/slices/documents-repository';
import type {
  Document,
  DocumentEntityType,
} from '@shared/data-access/types/engine-types';
import { useConfirmPopup } from '@shared/ui-kit/components';
import { useSnackbar } from '@shared/ui-kit/providers/snackbar-provider';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useDocuments } from '../../hooks/use-documents';
import { useDocumentsClientId } from '../../hooks/use-documents-client-id';
import { downloadDocumentFile } from '../../utils/download-document-files';
import {
  buildLinkedDocumentFileRow,
  filterRelatableDocuments,
  RELATABLE_DOCUMENTS_SEARCH_THRESHOLD,
  type LinkedDocumentFileRow,
} from './entity-documents-section.utils';

export interface EntityDocumentsSectionProps {
  readonly entityType: DocumentEntityType;
  readonly entityId: string;
  readonly disabled?: boolean;
}

export type { LinkedDocumentFileRow };

export { flattenLinkedDocumentFiles } from './entity-documents-section.utils';

export const useEntityDocumentsSection = ({
  entityType,
  entityId,
  disabled = false,
}: EntityDocumentsSectionProps) => {
  const { t } = useTranslation();
  const clientId = useDocumentsClientId();
  const { documentsQuery } = useDocuments();
  const showConfirm = useConfirmPopup();
  const { enqueueSnackbar } = useSnackbar();

  const [relatableSearchQuery, setRelatableSearchQuery] = useState('');
  const [downloadingRowKey, setDownloadingRowKey] = useState<string | null>(null);
  const [linkingDocumentId, setLinkingDocumentId] = useState<number | null>(null);

  const [updateDocument, updateDocumentState] =
    documentsApi.useUpdateDocumentMutation();
  const [fetchDocumentDetail] = documentsApi.useFetchDocumentDetailMutation();

  const linkedDocuments = useSelector((state: RootDocumentsRepositoryState) =>
    selectDocumentsLinkedToEntityForClient(state, clientId, entityType, entityId),
  );

  const clientDocuments = useSelector((state: RootDocumentsRepositoryState) =>
    selectDocumentsForClientScope(state, clientId),
  );

  const linkedDocumentIds = useMemo(
    () => new Set(linkedDocuments.map(document => document.id)),
    [linkedDocuments],
  );

  const relatableDocuments = useMemo(
    () => clientDocuments.filter(document => !linkedDocumentIds.has(document.id)),
    [clientDocuments, linkedDocumentIds],
  );

  const filteredRelatableDocuments = useMemo(
    () => filterRelatableDocuments(relatableDocuments, relatableSearchQuery),
    [relatableDocuments, relatableSearchQuery],
  );

  const hasEntityId = entityId !== '';
  const isCatalogLoading = documentsQuery.isLoading && clientDocuments.length === 0;

  const hasRelatableDocuments = relatableDocuments.length > 0;
  const showRelatableSearch =
    relatableDocuments.length > RELATABLE_DOCUMENTS_SEARCH_THRESHOLD;

  const showLinkPanel = !disabled && hasEntityId;

  const showUnlinkAction = !disabled && hasEntityId;

  const onLinkDocument = useCallback(
    async (documentId: number) => {
      if (
        !documentId ||
        !showLinkPanel ||
        linkingDocumentId !== null ||
        updateDocumentState.isLoading
      ) {
        return;
      }

      setLinkingDocumentId(documentId);

      try {
        await updateDocument({
          document_id: documentId,
          body: {
            relations_to_add: [
              {
                entity_type: entityType,
                entity_id: entityId,
              },
            ],
          },
        }).unwrap();
      } catch {
        enqueueSnackbar(
          t('documents.entity-section.link-error', { ns: 'components' }),
          { variant: 'error' },
        );
      } finally {
        setLinkingDocumentId(null);
      }
    },
    [
      enqueueSnackbar,
      entityId,
      entityType,
      linkingDocumentId,
      showLinkPanel,
      t,
      updateDocument,
      updateDocumentState.isLoading,
    ],
  );

  const onUnlinkDocument = useCallback(
    (document: Document) => {
      if (linkingDocumentId !== null || updateDocumentState.isLoading) {
        return;
      }

      showConfirm({
        title: t('documents.entity-section.unlink-confirm.title', {
          ns: 'components',
        }),
        message: t('documents.entity-section.unlink-confirm.message', {
          ns: 'components',
          title: document.title,
        }),
        confirmText: t('documents.entity-section.unlink', { ns: 'components' }),
        onConfirm: async () => {
          try {
            await updateDocument({
              document_id: document.id,
              body: {
                relations_to_remove: [
                  {
                    entity_type: entityType,
                    entity_id: entityId,
                  },
                ],
              },
            }).unwrap();
          } catch {
            enqueueSnackbar(
              t('documents.entity-section.unlink-error', { ns: 'components' }),
              { variant: 'error' },
            );
          }
        },
      });
    },
    [
      entityId,
      entityType,
      enqueueSnackbar,
      linkingDocumentId,
      showConfirm,
      t,
      updateDocument,
      updateDocumentState.isLoading,
    ],
  );

  const onDownloadFile = useCallback(
    async (row: LinkedDocumentFileRow) => {
      setDownloadingRowKey(row.rowKey);

      try {
        const detail = await fetchDocumentDetail({
          document_id: row.documentId,
        }).unwrap();

        const file = detail.files.find(
          detailFile => detailFile.file_name === row.fileName,
        );

        if (file?.url) {
          downloadDocumentFile(file.url, file.file_name);
        }
      } finally {
        setDownloadingRowKey(null);
      }
    },
    [fetchDocumentDetail],
  );

  const buildFileRowsForDocument = useCallback(
    (document: Document): LinkedDocumentFileRow[] =>
      (document.files ?? []).map((file, fileIndex) =>
        buildLinkedDocumentFileRow(document, file, fileIndex),
      ),
    [],
  );

  return {
    t,
    linkedDocuments,
    relatableDocuments: filteredRelatableDocuments,
    hasRelatableDocuments,
    showRelatableSearch,
    relatableSearchQuery,
    onRelatableSearchQueryChange: setRelatableSearchQuery,
    onLinkDocument,
    onUnlinkDocument,
    onDownloadFile,
    buildFileRowsForDocument,
    downloadingRowKey,
    linkingDocumentId,
    isCatalogLoading,
    showLinkPanel,
    showUnlinkAction,
    hasEntityId,
  };
};
